import {
  ColumnDataTypes,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type { NetworkConnectionListItem } from '@kirin/api-client/contracts/shadow-ai';
import { NETWORK_CONNECTIONS_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/shadow-ai';
import {
  StringOperatorsWithEmpty,
  NumberOperatorsWithEmpty,
  DateOperatorsWithEmpty,
} from '@/components/shared/shadow-ai/operators';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { queryKeys } from '@/hooks/api/queryKeys';
import { formatBytes } from '../shared';
import { NETWORK_DOMAINS_FILTER_URL_KEYS } from './constants';
import { fetchNetworkConnectionsFilterOptions } from './api';
import {
  NetworkStatusBadge,
  NetworkActionsCell,
  type NetworkDomainsActions,
} from './CellRenderers';
import type { NetworkStatusFilterValue } from './NetworkStatusFilterSelector';

/**
 * KIR-3496: Network Discovery domains table columns with per-column
 * Filter / Sort / Search, mirroring the Browser AI Tools table.
 *
 * `status` scopes the DYNAMIC filter dropdowns (Process/Host) so they only
 * offer values present in the current status view — matching the rows.
 */
export function createNetworkDomainsColumns(
  organizationId: string,
  options: {
    showAgentColumn: boolean;
    endpointLabel: string;
    status: NetworkStatusFilterValue;
  }
): ColumnConfig<NetworkConnectionListItem>[] {
  const { showAgentColumn, endpointLabel, status } = options;

  const agentColumn: ColumnConfig<NetworkConnectionListItem>[] = showAgentColumn
    ? [
        {
          id: 'agent',
          header: 'Agent',
          accessor: 'identifiedAgentName',
          width: 160,
          renderCell: ({ row }) => (
            <span className="font-medium">
              {row.identifiedAgentName || '-'}
            </span>
          ),
        },
      ]
    : [];

  return [
    ...agentColumn,
    {
      id: 'processName',
      header: 'Process',
      accessor: 'processName',
      width: 180,
      renderCell: ({ row }) => (
        <span className="font-mono text-sm text-blue-600 dark:text-blue-400">
          {row.processName || '-'}
        </span>
      ),
      sortable: true,
      sortField: 'processName',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.PROCESS
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.networkConnectionsFilterOptions(
            organizationId,
            'processName',
            { ...params, status }
          ),
        queryFn: params =>
          fetchNetworkConnectionsFilterOptions(
            organizationId,
            'processName',
            status,
            params
          ),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'domain',
      header: 'Host',
      accessor: 'domain',
      width: 240,
      renderCell: ({ row }) => (
        <span className="font-mono text-sm text-foreground">{row.domain}</span>
      ),
      sortable: true,
      sortField: 'domain',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HOST
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.shadowAi.networkConnectionsFilterOptions(
            organizationId,
            'domain',
            { ...params, status }
          ),
        queryFn: params =>
          fetchNetworkConnectionsFilterOptions(
            organizationId,
            'domain',
            status,
            params
          ),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      // Status is scoped by the top-level status selector, not a per-column
      // filter (KIR-3496) — this column is display + sort only.
      id: 'status',
      header: 'Status',
      accessor: 'status',
      width: 200,
      renderCell: ({ row }) => <NetworkStatusBadge connection={row} />,
      sortable: true,
      sortField: 'status',
    },
    {
      id: 'totalBytes',
      header: 'Traffic',
      accessor: 'totalBytesIn',
      width: 120,
      renderCell: ({ row }) => (
        <span className="font-mono text-sm">
          {formatBytes(row.totalBytesIn + row.totalBytesOut)}
        </span>
      ),
      sortable: true,
      sortField: 'totalBytes',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.TRAFFIC
          ],
        dataType: ColumnDataTypes.NUMBER,
      },
    },
    {
      id: 'hitCount',
      header: 'Hits',
      accessor: 'hitCount',
      width: 100,
      renderCell: ({ row }) => <span>{row.hitCount}</span>,
      sortable: true,
      sortField: 'hitCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HITS
          ],
        dataType: ColumnDataTypes.NUMBER,
      },
    },
    {
      id: 'uniqueUsers',
      header: 'Users',
      accessor: 'uniqueUsers',
      width: 100,
      renderCell: ({ row }) => <span>{row.uniqueUsers}</span>,
      sortable: true,
      sortField: 'uniqueUsers',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.USERS
          ],
        dataType: ColumnDataTypes.NUMBER,
      },
    },
    {
      id: 'uniqueMachines',
      header: endpointLabel,
      accessor: 'uniqueMachines',
      width: 110,
      renderCell: ({ row }) => <span>{row.uniqueMachines}</span>,
      sortable: true,
      sortField: 'uniqueMachines',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.MACHINES
          ],
        dataType: ColumnDataTypes.NUMBER,
      },
    },
    {
      id: 'lastSeen',
      header: 'Last Seen',
      accessor: 'lastSeen',
      width: 150,
      renderCell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          <RelativeTimeWithTooltip date={row.lastSeen} showIcon={false} />
        </span>
      ),
      sortable: true,
      sortField: 'lastSeen',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          NETWORK_DOMAINS_FILTER_URL_KEYS[
            NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}

/** Trailing Actions column (Identify / Dismiss / Revert). Not sortable/filterable. */
export function createNetworkDomainsActionsColumn(
  actions: NetworkDomainsActions
): ColumnConfig<NetworkConnectionListItem> {
  return {
    id: 'actions',
    header: 'Actions',
    accessor: 'domain',
    width: 180,
    renderCell: ({ row }) => <NetworkActionsCell row={row} actions={actions} />,
  };
}
